<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import iconChevron from "@core/assets/icons/chevron.svg?raw";
	import type { Country } from "@core/schema/Country.js";
	import { Currency } from "@core/schema/Currency.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { getPriceForCustomerOfProductVariant } from "@core/schema/utils/getPriceForCustomerOfProductVariant.js";
	import { getProductHeadingWithAttributes } from "@core/schema/utils/getProductHeadingWithAttributes.js";
	import { getProductOfVariant } from "@core/schema/utils/getProductOfVariant.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { buildProductAlt } from "../seo/buildProductAlt.js";
	import PreloadedPrice from "./PreloadedPrice.svelte";
	import translations from "./ProductB2bCard.translations.json" with { type: "json" };
	import ProductB2bModal from "./ProductB2bModal.svelte";
	import type { ProductForCard } from "./ProductCard.js";
	import ProductCardCartOptions from "./ProductCardCartOptions.svelte";
	import ProductCardMedia from "./ProductCardMedia.svelte";
	import ProductCardMediaFallback from "./ProductCardMediaFallback.svelte";
	import ProductTags from "./ProductTags.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Icon from "./common/Icon.svelte";
	import { getTagsForProductCard } from "./getTagsForProductCard.js";

	const { tenant } = getGlobalTemplateProps();
	const { product, subcategory }: { product: ProductForCard; subcategory: SubcategoryForCustomer } = $props();

	const primaryVariantId = $derived(product.variants[0]?.id);
	const productPrimaryVariant = $derived(primaryVariantId ? getProductOfVariant(product, primaryVariantId) : undefined);
	const mediaSrc = $derived(
		productPrimaryVariant ? getProductPrimaryMedia(productPrimaryVariant, product.attributes) : undefined,
	);

	let openDetailModal = $state(false);

	const heading = $derived(getProductHeadingWithAttributes(tenant.locale, product));
	const alt = $derived(buildProductAlt(product, subcategory, tenant.locale, product.attributes));
	const translate = getTranslate(translations);
</script>

<div class="grid grid-cols-subgrid col-span-full p-4 lg:p-6 lg:pr-10 border border-fcrlightgray-300 rounded-2xl">
	<button
		class="flex gap-3.5 xl:gap-6 items-center text-left"
		onclick={(): void => {
			openDetailModal = true;
		}}
	>
		<div
			class="bg-fcrlightgray-400 w-24 sm:w-33 md:w-42 lg:w-51 xl:w-60 aspect-square flex overflow-hidden flex-col justify-between p-1.5 sm:p-4 rounded-xl xl:rounded-2xl relative grow-0 shrink-0"
		>
			{#if productPrimaryVariant}
				<ProductTags tags={getTagsForProductCard({ product, locale: tenant.locale, subcategory })} />
				{#if mediaSrc}
					<ProductCardMedia {mediaSrc} {alt} />
				{:else}
					<ProductCardMediaFallback />
				{/if}
			{/if}
		</div>
		<div class="flex flex-col gap-1 xl:gap-2 grow shrink overflow-hidden xl:pr-12 2xl:pr-16">
			<div class="font-antipol text-4.5 xl:text-1.5xl text-fcrdarkgray flex items-center gap-2.5">
				<div>
					<Formatted text={heading} />
				</div>
				<Icon icon={iconChevron} class="size-6 -rotate-90" />
			</div>
			<div
				class="text-3.25/normal md:text-3.5/normal text-fcrgray line-clamp-2 sm:line-clamp-3 xl:line-clamp-4 w-0 min-w-full"
			>
				<Formatted text={product.plainDescription[tenant.locale]} />
			</div>
		</div>
	</button>
	<div
		class="grid grid-cols-3 xl:grid-cols-subgrid xl:col-span-3 self-center my-2 justify-start divide-y divide-fcrlightgray-300"
	>
		<div class="hidden xl:grid xl:grid-cols-subgrid xl:col-span-full text-fcrgray-100 size-4 font-medium mb-4 text-sm">
			<div><Formatted text={translate("variant")} /></div>
			<div class="pl-6 flex gap-2">
				<div class="xl:min-w-24 text-right text-nowrap"><Formatted text={translate("priceWithoutTax")} /></div>
				<div class="xl:min-w-24 text-right text-nowrap"><Formatted text={translate("priceWithTax")} /></div>
			</div>
			<div class="pl-6 text-right pr-10"><Formatted text={translate("quantity")} /></div>
		</div>
		{#each product.variants as variant (variant)}
			{@const productOfVariant = getProductOfVariant(product, variant.id)}
			{@const stockAmount = getProductStockAmount(product.stock, variant, product.attributes)}
			<div
				class="grid grid-cols-[1fr_1fr_2fr] xl:grid-cols-subgrid col-span-full items-center font-medium text-fcrdarkgray sm:gap-2 py-2"
			>
				<div
					class={[
						"text-xs min-[400px]:text-sm sm:text-base pr-2 sm:pr-7 shrink-0 sm:text-nowrap",
						stockAmount === 0 && "line-through",
					]}
				>
					{#if product.variantType !== ProductVariantType.None}
						<Formatted text={variant.label[tenant.locale]} />
					{/if}
				</div>
				<div class={["text-xs min-[400px]:text-sm sm:text-base", stockAmount === 0 && "line-through"]}>
					<PreloadedPrice class="h-6">
						{#snippet children(currency: Currency, destinationCountry: Country | "outsideEu")}
							{@const productPrice = getPriceForCustomerOfProductVariant(
								productOfVariant,
								currency,
								destinationCountry,
								EconomicSubjectVatType.NotVatSubject,
							)}
							<div class="flex flex-col xl:flex-row xl:gap-2 xl:items-baseline text-right">
								<div class="xl:min-w-24">
									{#if currency === Currency.CZK}
										<Formatted text={formatPrice(Math.ceil(productPrice.withoutTax), currency, tenant.locale, false)} />
									{:else}
										<Formatted text={formatPrice(productPrice.withoutTax, currency, tenant.locale, true)} />
									{/if}
								</div>
								<div
									class="text-fcrgray text-2.5 min-[400px]:text-3 sm:text-3.5 xl:[font-size:inherit] xl:min-w-24 font-normal"
								>
									{#if currency === Currency.CZK}
										<Formatted text={formatPrice(Math.ceil(productPrice.withTax), currency, tenant.locale, false)} />
									{:else}
										<Formatted text={formatPrice(productPrice.withTax, currency, tenant.locale, true)} />
									{/if}
									<span class="xl:hidden"><Formatted text={translate("withTax")} /></span>
								</div>
							</div>
						{/snippet}
					</PreloadedPrice>
				</div>
				<div class="w-42.5 flex items-center ml-auto pl-4">
					{#if stockAmount === 0}
						<div class="w-full text-center text-fcrgray">
							<Formatted text={translate("soldOut")} />
						</div>
					{:else}
						<ProductCardCartOptions
							showZeroCount
							showToasts
							shrinkOnMobile
							product={productOfVariant}
							attributes={product.attributes}
							max={stockAmount}
						/>
					{/if}
				</div>
			</div>
		{/each}
	</div>
</div>

{#if productPrimaryVariant}
	<ProductB2bModal {product} {productPrimaryVariant} {subcategory} bind:show={openDetailModal} />
{/if}
